import { useEffect, useRef, useId, type ReactNode } from 'react';
import { cn } from '../../lib/utils';
import { useFocusTrap } from '../../lib/use-focus-trap';

interface RowPeekDrawerProps {
  readonly isOpen: boolean;
  readonly onClose: () => void;
  /** Primary title shown at the top of the drawer (e.g. metro name). */
  readonly title: string;
  /** Optional uppercase eyebrow label (e.g. "METRO AREA"). */
  readonly eyebrow?: string;
  /** Caller-defined body content -- stats, nav links, etc. */
  readonly children: ReactNode;
  /**
   * Layout variant (2026-06-05 add, Maria):
   *   - `drawer` (default) — anchored to the right edge, slides in
   *     from off-screen. The original behavior used by Rankings and
   *     metro-peek surfaces.
   *   - `modal` — centered in the viewport, wider card with fade +
   *     scale entrance. Used by the Providers page where the body
   *     can render a long aligned-program list that benefits from
   *     a wider reading column.
   *
   * Focus management + a11y attrs are identical across variants;
   * only positioning, sizing, and the entrance transition differ.
   */
  readonly variant?: 'drawer' | 'modal';
}

/**
 * Dialog primitive for surfacing cross-tab navigation context from a
 * clicked table row. Modeled on the IntroModal a11y patterns
 * (per DEC-059). Renders one of two geometries via the `variant`
 * prop (see PR #87 / DEC-064 -- the modal variant was added so
 * Providers could surface a wider aligned-program reading column):
 *
 *   - `drawer` (default) -- right-anchored, 384px wide, full
 *     viewport height, slides in from off-screen with a horizontal
 *     `translate-x` transition.
 *   - `modal` -- centered card, up to 800px wide, fades + scales
 *     in (`opacity` + `scale`) from the viewport center.
 *
 * Both variants share the SAME a11y contract; only positioning,
 * sizing, and the entrance transition differ.
 *
 * Reused across Rankings (metro peek), Providers (provider peek),
 * and any future row-click surface that wants to expose nav links
 * + stats without yanking the user off the page.
 *
 * Render lifecycle (2026-06-05 / PR #87): the component
 * UNMOUNTS entirely when `isOpen` is false -- there is no
 * mounted-but-hidden state. The earlier behavior kept the panel in
 * the DOM with `opacity-0` + `translate-x-full` to play a close
 * animation, but the heavy shadow + backdrop-blur leaked through
 * on some browser configurations and left a ghost panel hovering
 * over the table. Trade-off: the close-out animation is dropped;
 * the open animations still play because the component remounts
 * fresh on the next open and CSS transitions only fire on a
 * property change after mount.
 *
 * Dismissal paths (all call `onClose`):
 *   - Close button (top-right, focused on open)
 *   - Backdrop click
 *   - Escape key
 *
 * A11y contract (identical for both variants):
 *   - role="dialog", aria-modal="true"
 *   - aria-labelledby points to the title id
 *   - Focus moves to the close button on open (deferred a frame so
 *     the panel is mounted before focus is requested)
 *   - Focus returns to the element that was active before open on
 *     close, if it still exists in the DOM
 *   - Focus is trapped within the dialog while open
 *     (Tab / Shift+Tab cycle via `useFocusTrap`)
 *   - Entrance transition respects `motion-reduce`
 *
 * The component itself owns no body content -- the caller passes
 * children (stats sections, RowPeekNavLink lists, etc.). Keeps the
 * primitive small and lets each caller compose the body for its
 * domain.
 */
export function RowPeekDrawer({
  isOpen,
  onClose,
  title,
  eyebrow,
  children,
  variant = 'drawer',
}: RowPeekDrawerProps) {
  const isModal = variant === 'modal';
  const titleId = useId();
  const panelRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  // Remember the element that opened the drawer so focus returns
  // there on close -- preserves keyboard context (the user's "I was
  // on row 7" mental model). Captured at the transition from closed
  // to open; cleared after focus is restored on close.
  const triggerElementRef = useRef<HTMLElement | null>(null);

  // Focus management: on open, remember the trigger and move focus
  // to the close button. On close, restore focus to the trigger.
  useEffect(() => {
    if (isOpen) {
      triggerElementRef.current = (document.activeElement as HTMLElement | null) ?? null;
      // requestAnimationFrame so focus moves after the panel mounts
      // and the slide transition starts -- a synchronous focus call
      // before the panel exists is a no-op.
      const handle = window.requestAnimationFrame(() => {
        closeButtonRef.current?.focus();
      });
      return () => window.cancelAnimationFrame(handle);
    }
    // Drawer is closed: restore focus to the trigger (if it still
    // exists in the DOM and is focusable).
    const trigger = triggerElementRef.current;
    if (trigger && typeof trigger.focus === 'function' && document.contains(trigger)) {
      trigger.focus();
    }
    triggerElementRef.current = null;
    return undefined;
  }, [isOpen]);

  // Escape closes the drawer.
  useEffect(() => {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', handleKey);
    return () => document.removeEventListener('keydown', handleKey);
  }, [isOpen, onClose]);

  // Trap Tab/Shift+Tab inside the drawer while it's open.
  useFocusTrap(panelRef, isOpen);

  // 2026-06-05 (Maria) bug fix: unmount entirely when closed, for
  // BOTH variants. The drawer's translate-x-full + opacity-0
  // approach left a ghost panel hovering over the table on some
  // browser configurations (heavy shadow + backdrop-blur paint
  // through opacity:0 / off-screen translates). Trade-off: we
  // lose the close-slide / fade-out animation, but the open
  // animations still play because the component remounts fresh
  // on the next click and css transitions only run on a property
  // change after mount.
  if (!isOpen) return null;

  // Past this point `isOpen` is always true (component returns null
  // when closed), so the rendered class lists below pin themselves to
  // the open-state variants -- no `isOpen ? open : closed` ternaries.
  return (
    <div
      className={cn(
        // z-[400] -- user-triggered drawer renders ABOVE SectionNav (z-[350]).
        // See the z-index ladder in CLAUDE.md for the full stack.
        'fixed inset-0 z-[400] pointer-events-auto',
      )}
    >
      {/* Backdrop -- click to dismiss. Fades in/out separately from
          the panel so users see the dim before the slide completes. */}
      <button
        type="button"
        tabIndex={-1}
        aria-hidden="true"
        onClick={onClose}
        className={cn(
          'absolute inset-0 w-full cursor-default bg-navy/30 backdrop-blur-[2px] opacity-100',
          'transition-opacity duration-300 ease-out motion-reduce:transition-none',
        )}
      />

      {/* Panel -- positioning + transition differ per variant.
          Drawer: right-anchored, slides in horizontally.
          Modal: centered, fades + scales in. Both share the same
          a11y attrs (role=dialog, aria-modal=true, aria-labelledby). */}
      <div
        ref={panelRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        className={cn(
          'flex flex-col border border-border bg-white',
          // Centered modal variant: positioning + size + entrance
          // fade/scale. Only emitted when isModal.
          isModal &&
            'absolute left-1/2 top-1/2 w-[min(800px,calc(100vw-32px))] -translate-x-1/2 -translate-y-1/2',
          isModal &&
            'max-h-[calc(100vh_-_var(--modal-inset))] rounded-overlay shadow-modal',
          isModal &&
            'transition-[transform,opacity] duration-200 ease-out motion-reduce:transition-none',
          // Side drawer variant: right-anchored, slides in from off-screen.
          !isModal &&
            'absolute right-0 top-0 h-full w-drawer max-w-[90vw] border-l shadow-drawer',
          !isModal &&
            'transition-transform duration-300 ease-out motion-reduce:transition-none',
          // Open-state classes per variant. `isOpen` is always true
          // here because the component unmounts when closed.
          isModal && 'opacity-100 scale-100',
          !isModal && 'translate-x-0',
        )}
      >
        <div className="flex items-start justify-between gap-3 border-b border-border-light p-6 pb-4">
          <div className="min-w-0">
            {eyebrow && (
              <p className="mb-1 text-eyebrow font-semibold uppercase text-text-4">
                {eyebrow}
              </p>
            )}
            <h2
              id={titleId}
              className="text-2xl font-bold leading-tight text-navy"
            >
              {title}
            </h2>
          </div>
          <button
            ref={closeButtonRef}
            type="button"
            onClick={onClose}
            aria-label="Close panel"
            className={cn(
              'shrink-0 flex h-8 w-8 items-center justify-center rounded-full',
              'bg-border-light text-text-2 transition hover:bg-border',
              'focus:outline-none focus:ring-2 focus:ring-blue/40',
            )}
          >
            <svg width="12" height="12" viewBox="0 0 10 10" aria-hidden="true" fill="none">
              <path
                d="M1 1L9 9M9 1L1 9"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>

        {/* Body -- scrollable, vertical stack. Caller controls
            content; we just provide the gap rhythm + padding. */}
        <div className="flex flex-1 flex-col gap-4 overflow-y-auto p-6 pt-4">
          {children}
        </div>
      </div>
    </div>
  );
}
